import { Box, Typography } from "@mui/material";
import { IPaginatedList } from "Interfaces/PaginatedList";
import { GridActionsCellItem, GridColDef, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useCallback, useEffect, useContext, useMemo, useState } from "react";
import { IUserState, setPageIndex, setPageSize, setSort } from "redux/features/userSlice";
import { IUser } from "Models/User";
import { Modal } from "components/Modal";
import { useNavigate } from "react-router-dom";
import { useDeleteUserMutation } from "redux/api/userApi";
import { useAppSelector } from "redux/store";
import styles from "scss/Table.module.scss";
import { toast } from "react-toastify";
import { ProfileAvatar } from "components/Profile/ProfileAvatar";
import { DataGrid } from "components/Shared";
import { useIntl } from "react-intl";
import { LanguageContext } from "LanguageProvider";

interface UserTableProps {
  paginatedQuery?: IPaginatedList<IUser>;
  isLoading: boolean;
}

export const Table = ({ paginatedQuery, isLoading }: UserTableProps) => {
  const { pageSize, sort, pageIndex} = useAppSelector(
    (state: { userState: IUserState }) => state.userState
  );
    const { formatMessage } = useIntl();
    const languageContext = useContext(LanguageContext);
    const locale = languageContext?.locale;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rows = paginatedQuery?.data ?? [];

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [userIdToDelete, setUserIdToDelete] = useState<string | undefined>(
    undefined
  );

  const [deleteUser, { isSuccess: isUserDeletedSuccess }] =
    useDeleteUserMutation();

  const handleSort = (newSortModel: GridSortModel) => {
    dispatch(setSort(newSortModel));
  };

  const handleChangePage = (newPage: number) => {
    dispatch(setPageIndex(newPage));
  };

  const handleChangeRowsPerPage = (newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
  };

  const onDeleteOpenModal = (userId: string) => {
    setOpenDeleteModal(true);
    setUserIdToDelete(userId);
  };

  const onCloseDeleteModal = () => {
    setUserIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onAcceptDeleteModal = () => {
    if (userIdToDelete) {
      onDelete(userIdToDelete);
    }
    setUserIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onEdit = useCallback(
    (userId: string) => {
      navigate("/users/" + userId);
    },
    [navigate]
  );

  const onDelete = useCallback(
    (userId: string) => {
      deleteUser(userId);
    },
    [deleteUser]
  );

  useEffect(() => {
    if (isUserDeletedSuccess) {
      toast.success("Usuario eliminado exitosamente");
    }
  }, [isUserDeletedSuccess]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "image",
        headerName: "",
        width: 80,
        renderCell(params) {
          const fullName = `${params.row.name.trim()[0]}${
            params.row.lastName ? params.row.lastName.trim()[0] : ""
          }`;
          return (
            <ProfileAvatar
              image={params.row.image}
              textToShow={fullName}
              styles={{
                width: "43px",
                height: "43px",
              }}
            />
          );
        },
        sortable: false,
      },
      {
        field: "name",
        renderCell(params) {
          return (
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.name}{" "}
              {params.row?.lastName ? params.row.lastName : ""}
            </Typography>
          );
        },
        headerName: formatMessage({ id: "user.name" }),
        flex: 1,
      },
      {
        field: "email",
        headerName: formatMessage({ id: "user.email" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.email}
            </Typography>
          );
        },
      },
      {
        field: "role",
        headerName: formatMessage({ id: "user.role" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {formatMessage({ id: `userRoles.${params.row.role}` })}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        width: 80,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(params.row.id)}
            label="Editar"
            placeholder={""}            
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => onDeleteOpenModal(params.row.id)}
            label="Borrar"
            placeholder={""}            
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
        ],
      },
    ],
    [onEdit, locale]
  );

  return (
    <Box className={styles.DataGridContainer}>
      <Modal.DeleteModal
        open={openDeleteModal}
        title={formatMessage({ id: "user.deleteModalMessage" })}
        onAcceptHandler={onAcceptDeleteModal}
        onCancelHandler={onCloseDeleteModal}
      />
      <DataGrid
        rows={rows}
        isLoading={isLoading}
        columns={columns}
        pageSize={pageSize}
        page={pageIndex}
        checkboxSelection
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleSort={handleSort}
        sortModel={sort}
        rowCount={paginatedQuery?.total}
      />
    </Box>
  );
};
